import { useLayoutEffect, useRef, useState } from "react";
import { ItemStats } from "../item/item-types";
import Border, { BorderType } from "../menu/border";

export type ItemTooltipProps = {
    itemStats: ItemStats;
    floating?: boolean;
};

const hasStats = (itemStats: ItemStats) => {
    return itemStats.health || itemStats.attack !== undefined || itemStats.armor || itemStats.speed;
};

const ItemTooltip: React.FC<ItemTooltipProps> = ({
    itemStats,
    floating = true
}) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<"below" | "above">("below");
    const [horizontal, setHorizontal] = useState<"center" | "left-edge" | "right-edge">("center");
    const [ready, setReady] = useState(false);

    useLayoutEffect(() => {
        if (!floating) {
            setReady(true);
            return;
        }
        const tooltip = tooltipRef.current;
        if (!tooltip) return;
        tooltip.style.visibility = "hidden";
        const rect = tooltip.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const spaceLeft = rect.left;
        const spaceRight = window.innerWidth - rect.right;

        if (spaceBelow < 0 && spaceAbove > rect.height) {
            setPosition("above");
        } else {
            setPosition("below");
        }

        if (spaceRight < 0) {
            setHorizontal("right-edge");
        } else if (spaceLeft < 0) {
            setHorizontal("left-edge");
        } else {
            setHorizontal("center");
        }
        setReady(true);
        tooltip.style.visibility = "";
    }, [floating]);

    let tooltipStyle: React.CSSProperties = floating
        ? { transform: "translateX(-50%)" }
        : {};

    if (floating) {
        if (horizontal === "left-edge") {
            tooltipStyle = { left: 0, transform: "none" };
        } else if (horizontal === "right-edge") {
            tooltipStyle = { right: 0, left: "auto", transform: "none" };
        }
    }

    return (
        (itemStats.name && (
            <div
                className={`item-tooltip${floating ? "" : " item-tooltip-static"}${floating && position === "above" ? " item-tooltip-above" : ""}`}
                ref={tooltipRef}
                style={{
                    ...tooltipStyle,
                    visibility: ready ? "visible" : "hidden"
                }}
            >
                <Border>
                    <div className='item-tooltip-content'>
                        <div className='item-tooltip-title'>
                            {itemStats.name}
                        </div>
                        <div className='item-tooltip-description'
                            dangerouslySetInnerHTML={{
                                __html: itemStats.description || ""
                            }}>
                        </div>
                        {hasStats(itemStats) &&
                            <div className='item-tooltip-stats'>
                                {itemStats.health && <div className='stat-health'>
                                    <img className='stat-icon' src='/resources/items/stats/icon_stats_health.png' alt='Health Stat Icon' />
                                    {itemStats.health}
                                </div>}
                                {itemStats.attack !== undefined && <div className='stat-attack'>
                                    <img className='stat-icon' src='/resources/items/stats/icon_stats_attack.png' alt='Attack Stat Icon' />
                                    {itemStats.attack}
                                </div>}
                                {itemStats.armor && <div className='stat-armor'>
                                    <img className='stat-icon' src='/resources/items/stats/icon_stats_armor.png' alt='Armor Stat Icon' />
                                    {itemStats.armor}
                                </div>}
                                {itemStats.speed && <div className='stat-speed'>
                                    <img className='stat-icon' src='/resources/items/stats/icon_stats_speed.png' alt='Speed Stat Icon' />
                                    {itemStats.speed}
                                </div>}
                            </div>
                        }
                        {itemStats.tags?.length && <div className='item-tooltip-tags'>
                            {itemStats.tags.map((tag, idx) => (
                                <span className='item-tag' key={idx}>
                                    <Border type={BorderType.Tag}>
                                        <span className='item-tag-text'>
                                            {tag}
                                        </span>
                                    </Border>
                                </span>
                            ))}
                        </div>}
                    </div>
                </Border>
            </div>
        )) || <></>
    );
};

export default ItemTooltip;